import React from 'react';
import { TableCell, TableRow, Typography } from '@mui/material';
import type { UsageVitalMetric, UsageVitalRow } from '../types';
import { LEER, formatCount, formatVitalValue, vitalRating } from '../services/usageFormat';
import { pivotVitals, VitalZelle, VITAL_METRICS } from '../services/vitalsPivot';
import SchutzZeile from './SchutzZeile';
import Bewertungspunkt from './Bewertungspunkt';
import DatenTabelle, { zahlenZelle } from './DatenTabelle';
import { nutzerText } from './SeitenTabelle';

/**
 * RITA-1515 (Nutzungserfassung Phase 3a) — Ladeleistung je Seite (Web Vitals).
 *
 * Eine Zeile je Seite, je Kennzahl Median und 95 % (services/vitalsPivot.ts faltet die Zeilen
 * der Lese-API zusammen). Die Einheit steht in der Kopfzeile, nicht in jeder Zelle: LCP, INP und
 * TTFB sind Millisekunden, CLS ist ein Bruchwert ohne Einheit. Vor jedem Wert steht die
 * Bewertung nach den Google-Schwellen als Punkt mit sprechendem Namen (gut /
 * verbesserungswürdig / schlecht) — eine Zahl wie „2,9 s“ sagt Fachanwendern sonst nichts.
 *
 * Das 95 % fehlt unter zehn Personen (es beschriebe eine Einzelperson) und wird oberhalb der
 * letzten Histogramm-Stufe durch die Textmarke des Servers ersetzt. Sammelzeilen tragen keine
 * Zellen: ein Median über „zu kleine Gruppen“ wäre eine Aussage über die verborgenen Seiten.
 */
export interface LadeleistungTabelleProps {
  rows: UsageVitalRow[];
}

const KENNZAHL_TITEL: Record<UsageVitalMetric, string> = {
  LCP: 'LCP · Laden bis größtes Element · ms',
  INP: 'INP · Reaktion auf Eingabe · ms',
  CLS: 'CLS · Layout-Sprünge · ohne Einheit',
  TTFB: 'TTFB · erstes Byte vom Server · ms',
};

/** Median oder 95 %: Zahl mit Bewertung, sonst Textmarke des Servers, sonst Strich. */
const wertInhalt = (metric: UsageVitalMetric, value: number | null, note: string | null = null): React.ReactNode => {
  if (value === null) return note ?? LEER;
  return (
    <>
      <Bewertungspunkt rating={vitalRating(metric, value)} />
      {formatVitalValue(metric, value)}
    </>
  );
};

const zellenFuer = (metric: UsageVitalMetric, zelle: VitalZelle | undefined, key: string) => [
  <TableCell key={`${key}-p50`} sx={zahlenZelle}>
    {zelle === undefined ? LEER : wertInhalt(metric, zelle.p50)}
  </TableCell>,
  <TableCell key={`${key}-p95`} sx={zahlenZelle}>
    {zelle === undefined ? LEER : wertInhalt(metric, zelle.p95, zelle.p95Note)}
  </TableCell>,
];

export default function LadeleistungTabelle({ rows }: LadeleistungTabelleProps) {
  const zeilen = pivotVitals(rows);
  return (
    <>
      <DatenTabelle
        leer={zeilen.length === 0}
        leerText="Im Zeitraum ist keine Ladeleistung verdichtet."
        kopfgruppen={[
          { titel: '', spalten: 3 },
          ...VITAL_METRICS.map((metric) => ({ titel: KENNZAHL_TITEL[metric], spalten: 2 })),
        ]}
        spalten={[
          { titel: 'Seite' },
          { titel: 'Nutzer', numerisch: true },
          { titel: 'Ladevorgänge', numerisch: true },
          ...VITAL_METRICS.flatMap(() => [
            { titel: 'Median', numerisch: true },
            { titel: '95 %', numerisch: true },
          ]),
        ]}
      >
        {zeilen.map((zeile, stelle) => (
          // Die Reihenfolge des Servers ist fest; die Stelle ist der stabile Schlüssel (wie SeitenTabelle).
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={`${zeile.route ?? 'sammel'}-${stelle}`}>
            <TableCell>
              <SchutzZeile
                suppressed={zeile.suppressed}
                groups={zeile.groups}
                users={zeile.users}
                bezeichner={zeile.route}
                technisch
              />
            </TableCell>
            <TableCell sx={zahlenZelle}>{nutzerText(zeile.users, zeile.usersEstimated)}</TableCell>
            <TableCell sx={zahlenZelle}>{formatCount(zeile.ladevorgaenge)}</TableCell>
            {VITAL_METRICS.flatMap((metric) => zellenFuer(metric, zeile.zellen[metric], `${stelle}-${metric}`))}
          </TableRow>
        ))}
      </DatenTabelle>
      {zeilen.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="p"
          sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}
          data-testid="nutzung-ladeleistung-legende"
        >
          <span>
            <Bewertungspunkt rating="gut" /> gut
          </span>
          <span>
            <Bewertungspunkt rating="verbesserungswürdig" /> verbesserungswürdig
          </span>
          <span>
            <Bewertungspunkt rating="schlecht" /> schlecht
          </span>
          <span>Schwellen nach Google: LCP 2,5 s / 4 s · INP 200 / 500 ms · CLS 0,1 / 0,25 · TTFB 800 / 1.800 ms</span>
        </Typography>
      )}
    </>
  );
}
